import { ResponsiveAutocomplete } from "@mozioinc/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebounce } from "usehooks-ts";
import { locationQuery } from "../utils/query";
import { ReactComponent as AirportSvg } from "./assets/airport.svg";
import type { Location } from "@mozioinc/query";

type LocationComboboxProps = {
  id?: string;
  label?: string;
  name?: string;
  error?: boolean;
  onBlur?: () => void;
  onValueChange?: (location: Location | null) => void;
  defaultValue?: string;
};

const LocationCombobox = ({
  id,
  label,
  name,
  error,
  onBlur,
  onValueChange,
  defaultValue = "",
}: LocationComboboxProps) => {
  const [query, setQuery] = useState(defaultValue);
  const debouncedQuery = useDebounce<string>(query, 400);

  const [loading, setLoading] = useState(false); // NOTE: this loading state is to include debounce time
  const { data: locations = [], isFetching } = useQuery({
    queryKey: ["locations", debouncedQuery],
    queryFn: () => locationQuery.queryAirports({ query: debouncedQuery }),
    enabled: debouncedQuery.length > 0,
  });

  const debounceDone = query === debouncedQuery;
  if (debounceDone && !isFetching && loading) {
    setLoading(false);
  }

  const handleInputChange = (value: string) => {
    if (value.length === 0) {
      return;
    }
    setQuery(value);
    setLoading(true);
  };

  const handleValueChange = (displayText: string) => {
    if (!displayText) {
      onValueChange?.(null);
      return;
    }

    const location = locations.find(
      (l) => l.displayText.toLowerCase() === displayText.toLowerCase(),
    );
    if (!location) {
      console.error(
        `Location not found for displayText: ${displayText}. This should not happen.`,
      );
      return;
    }

    onValueChange?.(location);
  };

  return (
    <ResponsiveAutocomplete
      id={id}
      name={name}
      label={label}
      items={locations.map((l) => ({
        value: l.displayText,
        label: l.displayText,
        icon: <AirportSvg />,
      }))}
      loading={loading}
      icon={<AirportSvg />}
      error={error}
      onValueChange={handleValueChange}
      onInputValueChange={handleInputChange}
      onBlur={onBlur}
      shouldFilter={false}
      defaultValue={query}
    />
  );
};

export { LocationCombobox };
